/**
 * Shared PixiJS class store.
 *
 * pixi.js is loaded lazily — only when WebGLRenderer._initPixi() runs — so
 * headless mode and the Canvas2D path never pay the bundle cost.  All WebGL
 * sub-modules import their Pixi constructors from here rather than re-importing
 * pixi.js (which would each trigger a separate dynamic import).
 *
 * Usage:
 *   1. _initPixi() calls setPixiClasses(await import('pixi.js')) early on.
 *   2. Sub-modules import { Sprite, Graphics, … } from './pixi.js'.
 *   3. Because ES module exports are live bindings, the sub-module reads will
 *      see the real classes by the time any drawing function is called.
 */

export let Application, Container, Sprite, Texture, Graphics, Matrix, CanvasSource,
    Mesh, Geometry, Shader, GlProgram, UniformGroup, Buffer, BlurFilter, RenderTexture;

/**
 * Called once by WebGLRenderer._initPixi() right after the dynamic import resolves.
 * @param {object} pixi  The pixi.js module namespace object.
 */
export function setPixiClasses(pixi) {
    ({
        Application, Container, Sprite, Texture, Graphics, Matrix, CanvasSource,
        Mesh, Geometry, Shader, GlProgram, UniformGroup, Buffer, BlurFilter,
        RenderTexture
    } = pixi);
}

// ---------------------------------------------------------------------------
// texFromCanvas — create a PixiJS Texture from an HTMLCanvasElement.
// PixiJS v8 requires CanvasSource; Texture.from(canvas) is unreliable in v8.
// Lives here (not shaders.js) because it depends on the lazy CanvasSource/Texture.
// ---------------------------------------------------------------------------

const _texCache = new Map();

/**
 * @param {HTMLCanvasElement} canvas
 * @returns {import('pixi.js').Texture | null}
 */
export function texFromCanvas(canvas) {
    if (!canvas) return null;
    if (!_texCache.has(canvas)) {
        const source  = new CanvasSource({ resource: canvas });
        const texture = new Texture({ source });
        _texCache.set(canvas, texture);
    }
    return _texCache.get(canvas);
}

/**
 * Destroy and evict the cached texture for a canvas.
 * MUST be called whenever a canvas that was passed to texFromCanvas() is
 * discarded or rebuilt (per-entity gradient canvases do this on every color
 * change) — the cache holds strong references, so skipping this pins the
 * canvas AND its GPU texture forever.
 * @param {HTMLCanvasElement|null|undefined} canvas
 */
export function releaseCanvasTexture(canvas) {
    if (!canvas) return;
    const texture = _texCache.get(canvas);
    if (texture) {
        texture.destroy(true); // true → also destroy the CanvasSource
        _texCache.delete(canvas);
    }
}

/**
 * Destroy every cached texture. Called from WebGLRenderer.destroy() so a
 * backend switch doesn't leave the whole cache pinned; textures are lazily
 * recreated if a WebGL renderer comes back.
 */
export function clearTexCache() {
    for (const texture of _texCache.values()) texture.destroy(true);
    _texCache.clear();
}
